<template>
  <section class="content d-flex">
    <div>
      <h1>Partner Notification</h1>

      <p>{{ useReleaseTemplate
        ? 'Notify partners when we upload a new version of the SDK.'
        : 'Send a notification to API or SDK partners that have an active credential.' }}</p>

      <div class="mb-12 composer-box">
        <v-form ref="composer" @submit.prevent="confirmSend()">
          <v-checkbox
            v-model="useReleaseTemplate"
            label="Use Release Email template"
            dense
            hide-details
            class="mt-0 mb-4"
          ></v-checkbox>

          <div v-if="!useReleaseTemplate">
            <p class="mb-1">Audience</p>
            <v-radio-group
              v-model="audience"
              :rules="validation.notEmptyList"
              row
              dense
              class="mt-0"
            >
              <v-radio label="API partners" :value="accessTypes.API"></v-radio>
              <v-radio label="SDK partners" :value="accessTypes.SDK"></v-radio>
            </v-radio-group>
          </div>

          <div v-if="useReleaseTemplate" class="d-flex align-baseline justify-space-between">
            <div class="mr-6 flex-grow-1">
              <p>Platform</p>
              <p class="mb-0">
                <v-select
                  required
                  outlined
                  dense
                  full-width
                  class="text-field-outlined-overrides"
                  v-model="release.platform"
                  :items="platforms"
                  :rules="validation.notEmptyList"
                ></v-select>
              </p>
            </div>

            <div class="flex-grow-1">
              <p>Version</p>
              <p class="mb-0">
                <v-text-field
                  required
                  outlined
                  dense
                  full-width
                  class="text-field-outlined-overrides"
                  v-model="release.version"
                  :rules="validation.notEmpty"
                  hint="A semver, e.g. 2.1.0"
                  persistent-hint
                ></v-text-field>
              </p>
            </div>
          </div>

          <v-divider class="mb-6 mt-2"></v-divider>

          <div>
            <p>Subject</p>
            <p class="mb-0">
              <v-text-field
                :readonly="useReleaseTemplate"
                required
                outlined
                dense
                class="text-field-outlined-overrides"
                v-model="subject"
                :rules="validation.notEmpty"
                counter="200"
                maxlength="200"
              ></v-text-field>
            </p>
          </div>

          <div>
            <p>Body</p>
            <p class="mb-0">
              <v-textarea
                :readonly="useReleaseTemplate"
                required
                outlined
                auto-grow
                rows="8"
                class="text-field-outlined-overrides"
                v-model="body"
                :rules="validation.notEmpty"
                :counter="useReleaseTemplate ? undefined : 10000"
                maxlength="10000"
                :hint="useReleaseTemplate ? '' : 'Plain text. Line breaks are preserved.'"
                persistent-hint
              ></v-textarea>
            </p>
          </div>

          <v-btn
            color="primary"
            type="submit"
            width="187"
            height="48"
            class="mr-4 mt-4"
          >{{ useReleaseTemplate ? 'Send Email' : 'Send Notification' }}</v-btn>
        </v-form>
      </div>

      <v-dialog
        v-model="dialog"
        width="600"
        height="300"
      >
        <v-card>
          <v-card-title class="pa-8">
            <h2 class="mb-0">Are you sure?</h2>
          </v-card-title>
          <v-card-text
            class="px-8 pb-8 grey--text text--darken-4"
          >
            <v-form @submit.prevent="send()">
              <p v-if="useReleaseTemplate">This will send an email to <strong><em>all users</em></strong> for <strong><em>every partner</em></strong> that has access to the SDK.</p>
              <p v-else>This will send an email to the technical contact and all users of <strong><em>every {{ audience }} partner</em></strong> that has an active credential.</p>

              <div class="mt-8">
                <v-btn
                  width="187"
                  height="48"
                  class="mr-5"
                  type="submit"
                  color="primary"
                  :loading="sending"
                >Send</v-btn>
                <v-btn
                  width="187"
                  height="48"
                  outlined
                  @click="dialog = false"
                >Cancel</v-btn>
              </div>
            </v-form>
          </v-card-text>
        </v-card>
      </v-dialog>
    </div>

    <v-divider vertical class="ml-8"></v-divider>

    <div class="ml-8 flex-grow-0">
      <h2 class="mb-0">Past Emails</h2>
      <p>Notifications and release emails we've already sent.</p>
      <v-divider class="mb-0 mt-2"></v-divider>

      <v-list>
        <v-list-item v-for="(item, index) in pastEmails" :key="index" class="px-0">
          <v-list-item-content>
            <v-list-item-title>
              <v-chip x-small label class="mr-2" :color="item.type === 'release' ? 'primary' : ''">
                {{ item.type === 'release' ? 'Release' : 'Free-form' }}
              </v-chip>
              {{ item.subject }}
            </v-list-item-title>
            <v-list-item-subtitle>
              <span v-if="item.type === 'release'">{{ item.platform }} {{ item.version }} &middot; </span>
              <span v-else>{{ item.audience }} &middot; {{ item.recipientCount }} sent &middot; </span>
              {{ Intl.DateTimeFormat('en-US', {dateStyle: 'long'}).format(new Date(item.createdAt)) }}
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </div>
  </section>
</template>

<script>
import constants from '@fbin-cp/august-constants';

import config from '../config';
import util from '../util';

export default {
  data: () => {
    return {
      useReleaseTemplate: false,
      audience: '',
      freeform: {
        subject: '',
        body: '',
      },
      release: {
        platform: '',
        version: '',
      },
      pastEmails: [],
      dialog: false,
      sending: false,
      platforms: ['iOS', 'Android'],
      accessTypes: constants.DeveloperAccessTypes,
      validation: config.formValidations,
    };
  },
  created: function () {
    this.getPastEmails();
  },
  computed: {
    subject: {
      get: function () {
        return this.useReleaseTemplate
          ? `New ${this.release.platform} SDK version has been uploaded`
          : this.freeform.subject;
      },
      set: function (value) {
        if (!this.useReleaseTemplate) {
          this.freeform.subject = value;
        }
      },
    },
    body: {
      get: function () {
        return this.useReleaseTemplate
          ? `Dear partner,\nWe are excited to share with you that version ${this.release.version} of ` +
              `the ${this.release.platform} SDK has been uploaded to developer.august.com. Please ` +
              `log in, then navigate to the “Releases” tab to find the new version.\n\nThanks,\nFBIN Team`
          : this.freeform.body;
      },
      set: function (value) {
        if (!this.useReleaseTemplate) {
          this.freeform.body = value;
        }
      },
    },
  },
  methods: {
    isFormValid: function () {
      return this.$refs.composer.validate();
    },
    confirmSend: function () {
      if (!this.isFormValid()) {
        return;
      }

      this.dialog = true;
    },
    getPastEmails: async function () {
      const options = {
        method: 'GET',
        ...config.defaultFetchOptions,
      };
      const [releaseResult, notificationResult] = await Promise.all([
        util.fetch(`${this.$store.state.apiHost}/releases/emails`, options),
        util.fetch(`${this.$store.state.apiHost}/notifications`, options),
      ]);

      const releases = releaseResult.ok
        ? (await releaseResult.json()).map((item) => ({
            type: 'release',
            subject: `New ${item.platform} SDK version has been uploaded`,
            platform: item.platform,
            version: item.version,
            createdAt: item.createdAt,
          }))
        : [];
      const notifications = notificationResult.ok
        ? (await notificationResult.json()).map((item) => ({ type: 'freeform', ...item }))
        : [];

      if (!releaseResult.ok || !notificationResult.ok) {
        this.$store.commit('snackbar', {text: 'Failed to get some past emails.', color: 'error'});
      }

      this.pastEmails = [...releases, ...notifications].sort(
        (lhs, rhs) => new Date(rhs.createdAt) - new Date(lhs.createdAt)
      );
    },
    send: async function () {
      if (!this.isFormValid()) {
        return;
      }

      this.sending = true;
      const result = this.useReleaseTemplate ? await this.sendReleaseEmail() : await this.sendNotification();
      this.sending = false;

      if (result.ok) {
        this.$store.commit('snackbar', {text: 'Sent.', color: 'success'});
        this.dialog = false;
        this.resetForm();
        await this.getPastEmails();
      } else {
        const error = await result.json();
        const text = error.message || 'Failed to send.';
        this.$store.commit('snackbar', {text, color: 'error'});
      }
    },
    sendReleaseEmail: function () {
      const formData = new FormData();
      formData.append('platform', this.release.platform);
      formData.append('version', this.release.version);

      const options = {
        method: 'POST',
        ...config.defaultFetchOptions,
        body: formData,
      };

      return util.fetch(`${this.$store.state.apiHost}/releases/email`, options);
    },
    sendNotification: function () {
      const options = {
        method: 'POST',
        ...config.defaultFetchOptions,
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({
          audience: this.audience,
          subject: this.subject,
          body: this.body,
        }),
      };

      return util.fetch(`${this.$store.state.apiHost}/notifications`, options);
    },
    resetForm: function () {
      this.$refs.composer.reset();
      this.useReleaseTemplate = false;
      this.audience = '';
      this.freeform = {subject: '', body: ''};
      this.release = {platform: '', version: ''};
    },
  },
};
</script>

<style lang="scss" scoped>
.composer-box {
  max-width: 600px;
}
</style>
